import { type ChangeEvent, type MouseEvent } from "react";
import {
  Box,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import type { ReportPeriodPreset } from "../utils/report-period";

export type ReportPeriodSelectorProps = {
  value: ReportPeriodPreset;
  onChange: (event: MouseEvent<HTMLElement>, value: ReportPeriodPreset | null) => void;
  customFrom: string;
  customTo: string;
  onCustomFromChange: (value: string) => void;
  onCustomToChange: (value: string) => void;
  /** Rótulo da seção (acessibilidade). */
  "aria-label"?: string;
};

const PRESETS: {
  value: ReportPeriodPreset;
  short: string;
  icon: typeof CalendarMonthOutlinedIcon;
}[] = [
  { value: "current_month", short: "Este mês", icon: CalendarMonthOutlinedIcon },
  { value: "last_month", short: "Anterior", icon: HistoryOutlinedIcon },
  { value: "last_3_months", short: "3 meses", icon: DateRangeOutlinedIcon },
  { value: "custom", short: "Datas", icon: EditCalendarOutlinedIcon },
];

/**
 * Seletor compacto de intervalo (estilo segmented control), alinhado ao painel de comissões / vendas.
 */
export function ReportPeriodSelector({
  value,
  onChange,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
  "aria-label": ariaLabel = "Intervalo do relatório",
}: ReportPeriodSelectorProps) {
  const theme = useTheme();
  const trackBg = alpha(theme.palette.primary.main, 0.07);
  const trackBorder = alpha(theme.palette.primary.main, 0.14);

  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.25}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontSize: "0.65rem",
            color: "text.secondary",
            flexShrink: 0,
            pt: { sm: 0.25 },
          }}
        >
          Intervalo
        </Typography>

        <Box
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            display: "flex",
            justifyContent: { xs: "stretch", sm: "flex-end" },
          }}
        >
          <Box
            sx={{
              overflowX: "auto",
              overflowY: "hidden",
              maxWidth: "100%",
              mx: { xs: -0.25, sm: 0 },
              pb: 0.25,
              "&::-webkit-scrollbar": { height: 3 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 8,
                bgcolor: alpha(theme.palette.text.secondary, 0.25),
              },
            }}
          >
            <ToggleButtonGroup
              value={value}
              exclusive
              onChange={onChange}
              aria-label={ariaLabel}
              sx={{
                display: "inline-flex",
                flexWrap: "nowrap",
                gap: 0,
                p: "0.35rem",
                bgcolor: trackBg,
                borderRadius: "14px",
                border: `1px solid ${trackBorder}`,
                boxShadow: `inset 0 1px 2px ${alpha("#000", 0.04)}`,
                "& .MuiToggleButtonGroup-grouped": {
                  border: "none !important",
                  borderRadius: "11px !important",
                  margin: "0 0.15rem !important",
                  px: { xs: 1, sm: 1.35 },
                  py: { xs: 0.6, sm: 0.65 },
                  minHeight: 0,
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                  lineHeight: 1.2,
                  color: "text.secondary",
                  transition: theme.transitions.create(
                    ["background-color", "color", "box-shadow"],
                    { duration: theme.transitions.duration.shorter }
                  ),
                  "&.Mui-selected": {
                    bgcolor: "background.paper",
                    color: "primary.main",
                    boxShadow: `0 1px 4px ${alpha("#000", 0.08)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.12)}`,
                    "&:hover": {
                      bgcolor: "background.paper",
                    },
                  },
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                  },
                },
              }}
            >
              {PRESETS.map(({ value: v, short, icon: Icon }) => (
                <ToggleButton key={v} value={v} disableRipple>
                  <Stack direction="row" alignItems="center" spacing={0.65} sx={{ whiteSpace: "nowrap" }}>
                    <Icon sx={{ fontSize: 15, opacity: value === v ? 1 : 0.65 }} />
                    <span>{short}</span>
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Stack>

      {value === "custom" ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", sm: "flex-end" }}
          sx={{
            flexWrap: "wrap",
            pt: 0.25,
            pl: { xs: 0, sm: 1.75 },
            ml: { sm: 0.25 },
            borderLeft: { sm: `3px solid ${alpha(theme.palette.primary.main, 0.35)}` },
          }}
        >
          <TextField
            type="date"
            size="small"
            label="De"
            value={customFrom}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onCustomFromChange(e.target.value)
            }
            InputLabelProps={{ shrink: true }}
            sx={{
              width: { xs: "100%", sm: 158 },
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.paper",
                fontSize: "0.8125rem",
              },
            }}
          />
          <TextField
            type="date"
            size="small"
            label="Até"
            value={customTo}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onCustomToChange(e.target.value)
            }
            InputLabelProps={{ shrink: true }}
            sx={{
              width: { xs: "100%", sm: 158 },
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.paper",
                fontSize: "0.8125rem",
              },
            }}
          />
        </Stack>
      ) : null}
    </Stack>
  );
}
